import { apiUrl } from './api-client'
import { AUTH_ERROR_CODES } from './auth-client'
import type { RequestOptions } from './api-client'
import type { AuthErrorCode, AuthFailure } from './auth-client'

/**
 * Getypeerde toegang tot de `/residences/:id/bookings`-endpoints van de
 * platform-API: de werkvoorraad van de verhuurder.
 *
 * Vier dingen liggen hier vast, in lijn met `api-client.ts`, `auth-client.ts` en
 * `media-client.ts`:
 *
 * 1. **Eén foutafhandeling.** Een aanroep werpt nooit; alles komt terug als een
 *    `BookingFailure` met een `kind` en — wanneer de API een herkenbare
 *    `{ error: { code, message } }` teruggaf — de foutcode uit het contract. Die
 *    codelijst is contractbreed en wordt hier niet opnieuw verzonnen.
 * 2. **De sessiecookie moet mee.** `siteshowroom_session` is httpOnly en gaat
 *    alleen mee met `credentials: 'include'`.
 * 3. **Geen aanname over een betaalprovider** (CLAUDE.md regel 9, §17.13). Een
 *    reservering zonder providerbetaling is normaal in `manual`-modus. Daarom is
 *    `paymentMode` hier `PaymentMode | null` en geen `PaymentMode` met een
 *    standaardwaarde: een modus die de API niet meldt wordt niet geraden, want
 *    "online" tonen bij een handmatige aanvraag is precies de verwarring die
 *    §17.13 wil voorkomen.
 * 4. **Geld faalt hard, tekst faalt zacht.** De antwoordcontrole is streng op wat
 *    dragend is (identiteit, periode, status, valuta en totaal) en mild op wat
 *    cosmetisch is (referentie, gastgegevens, notitie). Een ontbrekend bedrag
 *    stilzwijgend als € 0,00 tonen zou een verkeerd getal op het scherm zetten
 *    op precies het moment dat iemand beslist of het geld klopt.
 *
 * `exchange`, `httpFailure` en de kleine hulpfuncties staan letterlijk zo in
 * `media-client.ts` en `auth-client.ts`, maar zijn daar niet geëxporteerd. Dit is
 * de derde kopie; ze horen in een gedeelde `http.ts` thuis. Zodra die er is,
 * verdwijnt deze kopie mee.
 */

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/** Foutcodes zijn contractbreed; boekingen kennen er geen eigen. */
export type BookingErrorCode = AuthErrorCode

/** `ApiFailure` plus de foutcode van het contract — zie `AuthFailure`. */
export type BookingFailure = AuthFailure

export type BookingResult<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: BookingFailure }

/** Statussen uit `stays.status` (migratie 0005). */
export const BOOKING_STATUSES = ['hold', 'confirmed', 'blocked', 'cancelled', 'expired'] as const
export type BookingStatus = (typeof BOOKING_STATUSES)[number]

/** Betaalmodi uit `stays.payment_mode` (migratie 0014, §17.13). */
export const PAYMENT_MODES = ['online', 'manual'] as const
export type PaymentMode = (typeof PAYMENT_MODES)[number]

/** Herkomst uit `stays.source` (migratie 0005). */
export const BOOKING_SOURCES = ['direct', 'channel', 'manual', 'import'] as const
export type BookingSource = (typeof BOOKING_SOURCES)[number]

/**
 * De gast bij een reservering. Kan ontbreken: `stays.guest_id` is `ON DELETE SET
 * NULL` en een geanonimiseerde gast (`guests.anonymized_at`) laat de reservering
 * met opzet in stand zonder de gegevens.
 */
export interface BookingGuest {
  readonly email: string
  readonly fullName: string
  readonly phone: string
  readonly locale: string
}

/**
 * De prijsopbouw zoals die bij het boeken is vastgelegd — een momentopname, geen
 * herberekening. Alles in centen (regel 3); omrekenen naar euro's gebeurt
 * uitsluitend in de presentatielaag.
 */
export interface BookingPrice {
  readonly currency: string
  readonly nightlyRateCents: number
  readonly nightsTotalCents: number
  readonly cleaningFeeCents: number
  readonly extraGuestCents: number
  readonly discountCents: number
  /** Toeristenbelasting: in het totaal, maar geen omzet van de verhuurder. */
  readonly touristTaxCents: number
  readonly totalCents: number
  readonly depositCents: number
}

export interface Booking {
  /** De `stays.id`; hetzelfde id dat de actie-endpoints als `:stayId` verwachten. */
  readonly id: string
  readonly residenceId: string
  /** Leeg wanneer de API geen referentie levert; dan valt het scherm terug op het id. */
  readonly reference: string
  readonly status: BookingStatus
  readonly source: BookingSource | null
  /** `null` betekent: de API meldt de modus niet. Nooit raden — zie de kop. */
  readonly paymentMode: PaymentMode | null
  /** Kalenderdatums in de tijdzone van de residence, half-open [checkIn, checkOut). */
  readonly checkIn: string
  readonly checkOut: string
  readonly nights: number
  readonly guests: number | null
  readonly guest: BookingGuest | null
  readonly guestMessage: string
  readonly holdExpiresAt: string | null
  readonly confirmedAt: string | null
  readonly cancelledAt: string | null
  readonly cancellationReason: string
  /**
   * Wat er aan betaling is vastgelegd. `null` betekent: niets vastgelegd — bij
   * `manual` de normale gang van zaken zolang de verhuurder niet heeft bevestigd.
   */
  readonly paidCents: number | null
  readonly price: BookingPrice
  readonly createdAt: string | null
}

// ---------------------------------------------------------------------------
// Hulpfuncties (kopie uit media-client.ts; zie de kop van dit bestand)
// ---------------------------------------------------------------------------

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message
  }
  return String(cause)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isBookingErrorCode(value: string): value is BookingErrorCode {
  const known: readonly string[] = AUTH_ERROR_CODES
  return known.includes(value)
}

function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

/** Leest `{ error: { code, message } }` uit een foutantwoord; null als die vorm ontbreekt. */
function readErrorBody(payload: unknown): { code: BookingErrorCode; message: string } | null {
  if (!isRecord(payload)) {
    return null
  }
  const error = payload['error']
  if (!isRecord(error)) {
    return null
  }
  const code = error['code']
  if (typeof code !== 'string' || !isBookingErrorCode(code)) {
    return null
  }
  const message = error['message']
  return { code, message: typeof message === 'string' ? message : code }
}

/** Zet een niet-2xx-antwoord om naar een `BookingFailure`, mét foutcode wanneer die er is. */
function httpFailure(
  path: string,
  status: number,
  statusText: string,
  payload: unknown,
): BookingFailure {
  const suffix = statusText === '' ? '' : ` ${statusText}`
  const base = `${path}: HTTP ${String(status)}${suffix}`
  const body = readErrorBody(payload)
  if (body === null) {
    return { kind: 'http', status, message: base }
  }
  return { kind: 'http', status, code: body.code, message: `${base} — ${body.message}` }
}

function parseBody(raw: string): unknown {
  if (raw.trim() === '') {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

// ---------------------------------------------------------------------------
// Uitwisseling met de API
// ---------------------------------------------------------------------------

type Method = 'GET' | 'POST'

interface Exchange {
  readonly status: number
  readonly statusText: string
  readonly payload: unknown
  readonly roundTripMs: number
}

type ExchangeResult =
  | { readonly ok: true; readonly exchange: Exchange }
  | { readonly ok: false; readonly error: BookingFailure }

async function exchange(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<ExchangeResult> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  const payload = parseBody(raw)
  if (payload === undefined && response.ok) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: invalid JSON` } }
  }

  return {
    ok: true,
    exchange: {
      status: response.status,
      statusText: response.statusText,
      payload: payload ?? null,
      roundTripMs: performance.now() - startedAt,
    },
  }
}

// ---------------------------------------------------------------------------
// Validatie van de antwoordvorm
// ---------------------------------------------------------------------------
//
// Zelfde vorm als `describeHealthProblem` in `api-client.ts`: een omschrijving
// van het eerste probleem, of null. Zo wordt een afwijkend API-antwoord zichtbaar
// in plaats van als `undefined` op het scherm te belanden.

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

function isBookingStatus(value: unknown): value is BookingStatus {
  const known: readonly string[] = BOOKING_STATUSES
  return typeof value === 'string' && known.includes(value)
}

function isPaymentMode(value: unknown): value is PaymentMode {
  const known: readonly string[] = PAYMENT_MODES
  return typeof value === 'string' && known.includes(value)
}

function isBookingSource(value: unknown): value is BookingSource {
  const known: readonly string[] = BOOKING_SOURCES
  return typeof value === 'string' && known.includes(value)
}

/**
 * De prijsvelden staan óf plat op de reservering, óf gegroepeerd onder `price`
 * of `pricing`. Alle drie komen voor in het API-contract van stage 4; welke het
 * wordt is een keuze van de servicelaag en geen reden om dit scherm te laten
 * breken.
 */
function priceSource(value: Record<string, unknown>): Record<string, unknown> {
  const price = value['price']
  if (isRecord(price)) {
    return price
  }
  const pricing = value['pricing']
  if (isRecord(pricing)) {
    return pricing
  }
  return value
}

function describeBookingProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'booking is not an object'
  }
  if (typeof value['id'] !== 'string') {
    return 'missing "booking.id"'
  }
  for (const field of ['checkIn', 'checkOut'] as const) {
    const raw = value[field]
    if (typeof raw !== 'string' || !CALENDAR_DATE.test(raw)) {
      return `"booking.${field}" must be a YYYY-MM-DD date`
    }
  }
  // De database kent een CHECK-constraint op deze lijst; een andere waarde is een
  // contractbreuk, geen datavariatie, en hoort dus zichtbaar te falen.
  if (!isBookingStatus(value['status'])) {
    return '"booking.status" is not a known status'
  }
  const price = priceSource(value)
  if (typeof price['currency'] !== 'string') {
    return 'missing "booking.currency"'
  }
  // `Number.isSafeInteger` en niet `isFinite`: centen zijn per definitie geheel
  // (regel 3), en de geldformattering weigert terecht een gebroken bedrag. Zou
  // hier 12,5 cent binnenkomen, dan werpt de formatter midden in een render.
  if (!Number.isSafeInteger(price['totalCents'])) {
    return 'missing or non-integer "booking.totalCents"'
  }
  return null
}

/** Bedrag in centen; ontbreekt het veld, dan is het onderdeel er niet en telt het als 0. */
function readCents(source: Record<string, unknown>, field: string): number {
  const raw = source[field]
  return Number.isSafeInteger(raw) ? (raw as number) : 0
}

function readOptionalCents(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return Number.isSafeInteger(raw) ? (raw as number) : null
}

function readText(source: Record<string, unknown>, field: string): string {
  const raw = source[field]
  return typeof raw === 'string' ? raw : ''
}

function readTimestamp(source: Record<string, unknown>, field: string): string | null {
  const raw = source[field]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readGuest(value: unknown): BookingGuest | null {
  if (!isRecord(value)) {
    return null
  }
  const guest: BookingGuest = {
    email: readText(value, 'email'),
    fullName: readText(value, 'fullName'),
    phone: readText(value, 'phone'),
    locale: readText(value, 'locale'),
  }
  // Een gast zonder naam én zonder e-mail is geanonimiseerd of nooit ingevuld;
  // dan is er niets te tonen en is `null` eerlijker dan een leeg blok.
  if (guest.email === '' && guest.fullName === '') {
    return null
  }
  return guest
}

function toBooking(value: Record<string, unknown>, residenceId: string): Booking {
  const price = priceSource(value)
  // De vorm is hierboven runtime-gecontroleerd; de casts maken dat expliciet.
  const checkIn = value['checkIn'] as string
  const checkOut = value['checkOut'] as string
  const nights = value['nights']

  return {
    id: value['id'] as string,
    residenceId: typeof value['residenceId'] === 'string' ? value['residenceId'] : residenceId,
    reference: readText(value, 'reference'),
    status: value['status'] as BookingStatus,
    source: isBookingSource(value['source']) ? value['source'] : null,
    paymentMode: isPaymentMode(value['paymentMode']) ? value['paymentMode'] : null,
    checkIn,
    checkOut,
    nights:
      typeof nights === 'number' && Number.isFinite(nights)
        ? nights
        : countNights(checkIn, checkOut),
    guests: typeof value['guests'] === 'number' ? value['guests'] : null,
    guest: readGuest(value['guest']),
    guestMessage: readText(value, 'guestMessage'),
    holdExpiresAt: readTimestamp(value, 'holdExpiresAt'),
    confirmedAt: readTimestamp(value, 'confirmedAt'),
    cancelledAt: readTimestamp(value, 'cancelledAt'),
    cancellationReason: readText(value, 'cancellationReason'),
    paidCents: readOptionalCents(value, 'paidCents'),
    price: {
      currency: price['currency'] as string,
      nightlyRateCents: readCents(price, 'nightlyRateCents'),
      nightsTotalCents: readCents(price, 'nightsTotalCents'),
      cleaningFeeCents: readCents(price, 'cleaningFeeCents'),
      extraGuestCents: readCents(price, 'extraGuestCents'),
      discountCents: readCents(price, 'discountCents'),
      touristTaxCents: readCents(price, 'touristTaxCents'),
      totalCents: price['totalCents'] as number,
      depositCents: readCents(price, 'depositCents'),
    },
    createdAt: readTimestamp(value, 'createdAt'),
  }
}

type BookingEnvelope =
  | { readonly ok: true; readonly booking: Booking }
  | { readonly ok: false; readonly error: BookingFailure }

function readBookingEnvelope(path: string, payload: unknown, residenceId: string): BookingEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const problem = describeBookingProblem(payload['booking'])
  if (problem !== null) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
  }
  return {
    ok: true,
    booking: toBooking(payload['booking'] as Record<string, unknown>, residenceId),
  }
}

type BookingListEnvelope =
  | { readonly ok: true; readonly bookings: readonly Booking[] }
  | { readonly ok: false; readonly error: BookingFailure }

function readBookingListEnvelope(
  path: string,
  payload: unknown,
  residenceId: string,
): BookingListEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const list = payload['bookings']
  if (!Array.isArray(list)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: missing "bookings"` },
    }
  }
  const bookings: Booking[] = []
  for (const entry of list) {
    const problem = describeBookingProblem(entry)
    if (problem !== null) {
      return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
    }
    bookings.push(toBooking(entry as Record<string, unknown>, residenceId))
  }
  return { ok: true, bookings }
}

// ---------------------------------------------------------------------------
// Rekenwerk zonder presentatie
// ---------------------------------------------------------------------------

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000

/**
 * Aantal nachten in een half-open periode [checkIn, checkOut).
 *
 * De datums worden nadrukkelijk als UTC-middernacht gelezen. Zou je ze in de
 * tijdzone van de browser lezen, dan levert een zomertijdovergang binnen de
 * periode 23 of 25 uur op en telt een verblijf een nacht te weinig of te veel.
 */
export function countNights(checkIn: string, checkOut: string): number {
  const from = Date.parse(`${checkIn}T00:00:00Z`)
  const to = Date.parse(`${checkOut}T00:00:00Z`)
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) {
    return 0
  }
  return Math.round((to - from) / MS_PER_DAY)
}

/** Onder deze grens is een aanvraag dringend: er blijft minder dan een halve dag over. */
export const HOLD_URGENT_MS = 12 * MS_PER_HOUR
/** Onder deze grens loopt de termijn zichtbaar af, maar is er nog een dag. */
export const HOLD_SOON_MS = 24 * MS_PER_HOUR

export type HoldTone = 'expired' | 'urgent' | 'soon' | 'ok'
export type HoldUnit = 'minute' | 'hour' | 'day'

export interface HoldCountdown {
  readonly tone: HoldTone
  readonly unit: HoldUnit
  /** Naar beneden afgerond: liever minder tijd tonen dan er is, nooit meer. */
  readonly count: number
  readonly remainingMs: number
}

/**
 * Hoe lang de vervaltermijn van een aanvraag nog loopt (§17.13).
 *
 * `null` betekent dat er geen bruikbare termijn is. Dat hoort niet voor te komen
 * bij een `hold` — `stays_hold_has_expiry` eist er een — maar een aanvraag zonder
 * zichtbare termijn is precies het geval waarvoor die constraint bestaat, dus het
 * scherm moet ermee om kunnen gaan zonder een verzonnen deadline te tonen.
 */
export function describeHold(holdExpiresAt: string | null, now: number): HoldCountdown | null {
  if (holdExpiresAt === null) {
    return null
  }
  const expiresAt = Date.parse(holdExpiresAt)
  if (Number.isNaN(expiresAt)) {
    return null
  }
  const remainingMs = expiresAt - now
  if (remainingMs <= 0) {
    return { tone: 'expired', unit: 'minute', count: 0, remainingMs }
  }
  const tone: HoldTone =
    remainingMs <= HOLD_URGENT_MS ? 'urgent' : remainingMs <= HOLD_SOON_MS ? 'soon' : 'ok'
  if (remainingMs < MS_PER_HOUR) {
    return { tone, unit: 'minute', count: Math.floor(remainingMs / MS_PER_MINUTE), remainingMs }
  }
  if (remainingMs < 2 * MS_PER_DAY) {
    return { tone, unit: 'hour', count: Math.floor(remainingMs / MS_PER_HOUR), remainingMs }
  }
  return { tone, unit: 'day', count: Math.floor(remainingMs / MS_PER_DAY), remainingMs }
}

/**
 * Openstaande aanvragen op volgorde van urgentie: wat het eerst verloopt staat
 * bovenaan, een aanvraag zonder termijn onderaan. Bij gelijke termijn beslist de
 * aankomstdatum, zodat de volgorde tussen twee ophaalacties niet verspringt.
 */
export function sortByDeadline(bookings: readonly Booking[]): readonly Booking[] {
  return [...bookings].sort((left, right) => {
    const leftAt = left.holdExpiresAt === null ? Infinity : Date.parse(left.holdExpiresAt)
    const rightAt = right.holdExpiresAt === null ? Infinity : Date.parse(right.holdExpiresAt)
    const leftKey = Number.isNaN(leftAt) ? Infinity : leftAt
    const rightKey = Number.isNaN(rightAt) ? Infinity : rightAt
    if (leftKey !== rightKey) {
      return leftKey - rightKey
    }
    return left.checkIn.localeCompare(right.checkIn)
  })
}

/** Bevestigde reserveringen op aankomstdatum: de eerstvolgende gast bovenaan. */
export function sortByArrival(bookings: readonly Booking[]): readonly Booking[] {
  return [...bookings].sort((left, right) => left.checkIn.localeCompare(right.checkIn))
}

/**
 * Leest een ingevoerd bedrag als een geheel aantal centen, of `null` wanneer het
 * geen bedrag is.
 *
 * Bewust op de string en niet via `Number(...) * 100`: dat laatste is floating
 * point en levert voor 12,10 euro 1209,9999999999998 centen op. Dit is de enige
 * plek waar een bedrag van buiten binnenkomt, dus hier hoort het exact te gaan
 * (regel 3). Zowel de punt als de komma wordt geaccepteerd; een `input[type=number]`
 * levert altijd een punt, maar geplakte tekst niet.
 */
export function parseAmountToCents(input: string): number | null {
  const trimmed = input.replace(/\s/g, '')
  if (trimmed === '') {
    return null
  }
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(trimmed)
  if (match === null) {
    return null
  }
  const whole = match[1] ?? '0'
  const fraction = (match[2] ?? '').padEnd(2, '0')
  const cents = Number(whole) * 100 + Number(fraction)
  return Number.isSafeInteger(cents) ? cents : null
}

/** Centen naar de waarde van een `input[type=number]`: altijd een punt, altijd twee cijfers. */
export function formatCentsForInput(cents: number): string {
  const safe = Number.isSafeInteger(cents) && cents >= 0 ? cents : 0
  const whole = Math.floor(safe / 100)
  const fraction = String(safe % 100).padStart(2, '0')
  return `${String(whole)}.${fraction}`
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

function bookingsPath(residenceId: string): string {
  return `/residences/${encodeURIComponent(residenceId)}/bookings`
}

function actionPath(residenceId: string, stayId: string, action: string): string {
  return `${bookingsPath(residenceId)}/${encodeURIComponent(stayId)}/${action}`
}

export interface ListBookingsOptions extends RequestOptions {
  readonly status?: BookingStatus
}

/** `GET /residences/:id/bookings?status=` */
export async function listBookings(
  residenceId: string,
  options: ListBookingsOptions = {},
): Promise<BookingResult<readonly Booking[]>> {
  const base = bookingsPath(residenceId)
  const path =
    options.status === undefined ? base : `${base}?status=${encodeURIComponent(options.status)}`
  const result = await exchange('GET', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readBookingListEnvelope(path, ex.payload, residenceId)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.bookings, roundTripMs: ex.roundTripMs }
}

/** Wat de verhuurder bij een handmatige bevestiging vastlegt (§17.13). */
export interface ConfirmInput {
  /** Ontvangen bedrag in centen. Weglaten betekent: geen bedrag vastleggen. */
  readonly amountCents?: number
  readonly note?: string
}

/**
 * `POST /residences/:id/bookings/:stayId/confirm`
 *
 * Bevestiging en betaling zijn twee gebeurtenissen (§17.13): dit endpoint legt ze
 * allebei vast, maar het bedrag is optioneel — bevestigen zonder een bedrag te
 * noteren is geldig, en een reservering zonder providerbetaling is in
 * `manual`-modus normaal (regel 9).
 */
export async function confirmBooking(
  residenceId: string,
  stayId: string,
  input: ConfirmInput,
  options: RequestOptions = {},
): Promise<BookingResult<Booking>> {
  const path = actionPath(residenceId, stayId, 'confirm')
  // Alleen meesturen wat werkelijk is ingevuld; een lege notitie of een leeg
  // bedrag is iets anders dan de waarde 0.
  const body: Record<string, unknown> = {}
  if (input.amountCents !== undefined) {
    body['amountCents'] = input.amountCents
  }
  if (input.note !== undefined && input.note !== '') {
    body['note'] = input.note
  }
  return runAction(path, body, residenceId, options)
}

/** `POST /residences/:id/bookings/:stayId/decline` — afwijzen met reden; stilte is geen optie. */
export async function declineBooking(
  residenceId: string,
  stayId: string,
  reason: string,
  options: RequestOptions = {},
): Promise<BookingResult<Booking>> {
  return runAction(actionPath(residenceId, stayId, 'decline'), { reason }, residenceId, options)
}

/** `POST /residences/:id/bookings/:stayId/extend` — de vervaltermijn oprekken. */
export async function extendBooking(
  residenceId: string,
  stayId: string,
  hours: number,
  options: RequestOptions = {},
): Promise<BookingResult<Booking>> {
  return runAction(actionPath(residenceId, stayId, 'extend'), { hours }, residenceId, options)
}

/**
 * `POST /residences/:id/bookings/:stayId/cancel`
 *
 * Voor de bevestigde reservering waarvan het geld alsnog niet binnenkomt. Dat is
 * een annulering met reden en geen verwijdering (§17.13): de reservering blijft
 * zichtbaar, inclusief waarom zij niet doorging.
 */
/**
 * Waar de gast recht op heeft na een annulering.
 *
 * Het platform betaalt niet terug — dat doet de verhuurder, op zijn eigen rekening
 * (§17.11). Dit is dus een bedrag om te betalen en geen bevestiging dat het al is
 * gebeurd, en de tekst op het scherm zegt dat ook zo.
 */
export interface RefundEntitlement {
  readonly amountCents: number
  readonly nightsRefundPermille: number
  readonly cleaningRefunded: boolean
  /** Welke regel is toegepast; de frontend vertaalt deze sleutel. */
  readonly reason: string
}

/** Leest de aanspraak uit het antwoord op de annuleerroute; null wanneer hij ontbreekt. */
function readRefund(payload: unknown): RefundEntitlement | null {
  if (!isRecord(payload) || !isRecord(payload['refund'])) {
    return null
  }
  const refund = payload['refund']
  const amountCents = refund['amountCents']
  if (!Number.isSafeInteger(amountCents)) {
    return null
  }
  return {
    amountCents: amountCents as number,
    nightsRefundPermille: readOptionalCents(refund, 'nightsRefundPermille') ?? 0,
    cleaningRefunded: refund['cleaningRefunded'] === true,
    reason: readText(refund, 'reason'),
  }
}

export interface CancelOutcome {
  readonly booking: Booking
  /**
   * Null wanneer er geen voorwaarde gold. Dat is iets anders dan een aanspraak van
   * nul, en het scherm zegt het ook anders.
   */
  readonly refund: RefundEntitlement | null
}

/**
 * Annuleren, met de aanspraak van de gast erbij.
 *
 * Eigen uitwisseling en niet `runAction`: die leest alleen de reservering uit het
 * antwoord, en juist het bedrag is hier wat de verhuurder nodig heeft op het moment
 * dat hij annuleert.
 */
export async function cancelBooking(
  residenceId: string,
  stayId: string,
  reason: string,
  options: RequestOptions = {},
): Promise<BookingResult<CancelOutcome>> {
  const path = actionPath(residenceId, stayId, 'cancel')
  const result = await exchange('POST', path, { reason }, options)
  if (!result.ok) {
    return result
  }

  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }

  const envelope = readBookingEnvelope(path, ex.payload, residenceId)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }

  return {
    ok: true,
    data: { booking: envelope.booking, refund: readRefund(ex.payload) },
    roundTripMs: ex.roundTripMs,
  }
}

async function runAction(
  path: string,
  body: Record<string, unknown>,
  residenceId: string,
  options: RequestOptions,
): Promise<BookingResult<Booking>> {
  const result = await exchange('POST', path, body, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readBookingEnvelope(path, ex.payload, residenceId)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.booking, roundTripMs: ex.roundTripMs }
}

// ---------------------------------------------------------------------------
// Van fout naar vertaalsleutel
// ---------------------------------------------------------------------------

/**
 * Vertaalt een mislukking naar de **sleutel** van de melding die de gebruiker
 * ziet — nooit naar tekst; user-facing tekst hoort uitsluitend in de catalogi van
 * `packages/i18n` (§17.8). Zelfde opzet als `mediaErrorMessageKey`.
 *
 * `conflict` is hier de belangrijkste: die betekent dat de aanvraag intussen al
 * is afgehandeld of verlopen — twee tabbladen, of de automatische vervaltermijn
 * die net eerder was. Het scherm haalt daarna opnieuw op, dus de melding zegt dat
 * ook.
 */
export function bookingErrorMessageKey(failure: BookingFailure): string {
  switch (failure.code) {
    case 'conflict':
      return 'dashboard.bookings.error.conflict'
    case 'validation_failed':
      return 'dashboard.bookings.error.rejected'
    case 'not_found':
      return 'dashboard.bookings.error.notFound'
    case 'forbidden':
      return 'dashboard.bookings.error.forbidden'
    case 'rate_limited':
      return 'dashboard.bookings.error.rateLimited'
    default:
      // invalid_credentials, mfa_required, internal, en alles zonder code
      // (netwerkfout, onleesbaar antwoord): voor de gebruiker is dat één en
      // dezelfde situatie — het lukt nu niet.
      return 'dashboard.bookings.error.unavailable'
  }
}
